import React, { useState, useEffect, useRef } from 'react'
import { Select, PersonEditForm } from '../'
import { useDebounce, useModalDialog } from '../../hooks'
import { personService } from '../../services'

const toOption = (p) => ({
  value: p.person_uuid,
  label: p.person_name,
  secondaryLabel: [p.person_email, p.company_name, p.person_title].filter(Boolean).join(' • '),
})

// Two effects/callbacks below can each prepend the same person (the
// "resolve preselected uuid" effect and a freshly-created person's
// onSaved/onChange) — dedupe so <Select> never gets two options with the
// same key.
const prependOption = (prev, item) => prev.some(o => o.value === item.value) ? prev : [item, ...prev]

// Wraps <Select> in server-search mode against /api/network/person/search.
// allowCreate adds a "+ Skapa "<term>" som ny person" option that opens the
// exact same create form as the Nätverk person page (shared PersonEditForm)
// in the global modal — same identifier rule, duplicate-error callout, etc.
// A duplicate identifier comes back as a 409 naming the existing person;
// shown inline in the modal rather than silently merging.
export const PersonPicker = ({
  name,
  value,
  onChange,
  label,
  placeholder = 'Sök person...',
  allowCreate = false,
  onCreateRequest,
  isNullable = false,
  disabled = false,
  excludeInternal = false,
}) => {
  const { showModalDialog, hideModalDialog, setLoadingModalDialog } = useModalDialog()
  const dialogRef = useRef({ submit: () => Promise.resolve() })

  const [searchTerm, setSearchTerm] = useState('')
  const [options, setOptions] = useState([])
  const [resultCount, setResultCount] = useState(0)
  const [loading, setLoading] = useState(false)

  const { debounce } = useDebounce((term) => runSearch(term), 250, [excludeInternal])

  const runSearch = (term) => {
    setLoading(true)
    personService.search(term)
      .then(res => {
        let persons = res.persons || []
        if (excludeInternal) persons = persons.filter(p => !p.is_internal)
        setOptions(persons.map(toOption))
        setResultCount(res.resultCount || persons.length)
      })
      .finally(() => setLoading(false))
  }

  useEffect(() => { runSearch('') }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Resolve a preselected uuid to a label even if it's outside the current search results
  useEffect(() => {
    if (value && !options.find(o => o.value === value)) {
      personService.getByUuid(value).then(res => {
        if (res.person) setOptions(prev => prependOption(prev, toOption(res.person)))
      }).catch(() => {})
    }
  }, [value]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleInputChange = (term) => {
    setSearchTerm(term)
    debounce(term)
  }

  const handleChange = ({ value: selectedVal }) => {
    if (selectedVal === '__create__') {
      openCreateDialog(searchTerm)
      return
    }
    const person = options.find(o => o.value === selectedVal)
    onChange({ name, value: selectedVal || '', person: person ? { person_uuid: person.value, person_name: person.label } : null })
  }

  const openCreateDialog = (term) => {
    if (onCreateRequest) return onCreateRequest(term)

    showModalDialog({
      title: 'Skapa ny person',
      Content: () => (
        <PersonEditForm
          initialName={term}
          dialogRef={dialogRef}
          hideModalDialog={hideModalDialog}
          setLoadingModalDialog={setLoadingModalDialog}
          onSaved={(person) => {
            setOptions(prev => prependOption(prev, toOption(person)))
            onChange({ name, value: person.person_uuid, person })
          }}
        />
      ),
      submitLabel: 'Skapa',
      submitLabelLoading: 'Skapar...',
      cancelLabel: 'Avbryt',
      onSubmit: () => dialogRef.current.submit(),
      onCancel: hideModalDialog,
    })
  }

  const createOption = allowCreate && searchTerm
    ? [{ value: '__create__', label: `+ Skapa "${searchTerm}" som ny person` }]
    : []

  return (
    <Select
      name={name}
      label={label}
      value={value || ''}
      options={[...options, ...createOption]}
      searchTerm={searchTerm}
      loading={loading}
      resultCount={resultCount}
      onInputChange={handleInputChange}
      onChange={handleChange}
      isNullable={isNullable}
      disabled={disabled}
      placeholder={placeholder}
    />
  )
}
